const STORAGE_KEY = 'inboxpulse_analysis_sessions_v1';

export function loadSessions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export function persistSessions(sessions) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
}

export function createSession(results) {
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    title: `Analysis · ${new Date().toLocaleString()}`,
    results: Array.isArray(results) ? results : [],
  };
}

/** One result per Gmail message id; if duplicates exist, keep the last in list order. */
export function dedupeResultsByEmailId(results) {
  if (!Array.isArray(results) || results.length === 0) return [];
  const lastById = new Map();
  const noId = [];
  for (const r of results) {
    const id = r?.email?.id;
    if (!id) {
      noId.push(r);
      continue;
    }
    lastById.set(id, r);
  }
  const order = [];
  const included = new Set();
  for (let i = results.length - 1; i >= 0; i--) {
    const id = results[i]?.email?.id;
    if (id && !included.has(id)) {
      order.push(id);
      included.add(id);
    }
  }
  order.reverse();
  const out = order.map((id) => lastById.get(id));
  return [...out, ...noId];
}

/**
 * Remove duplicate analyses for the same email across saved runs: newest run (by createdAt) wins.
 * Drops sessions that end up with no results.
 */
export function normalizeSessionsRemoveDuplicates(sessions) {
  if (!Array.isArray(sessions) || sessions.length === 0) return [];
  const withInnerDeduped = sessions.map((s) => ({
    ...s,
    results: dedupeResultsByEmailId(s.results || []),
  }));
  const newestFirst = [...withInnerDeduped].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const claimed = new Set();
  const idToFiltered = new Map();
  for (const s of newestFirst) {
    const kept = [];
    for (const r of s.results) {
      const id = r?.email?.id;
      if (!id) {
        kept.push(r);
        continue;
      }
      if (claimed.has(id)) continue;
      claimed.add(id);
      kept.push(r);
    }
    idToFiltered.set(s.id, kept);
  }
  return withInnerDeduped
    .map((s) => ({ ...s, results: idToFiltered.get(s.id) || [] }))
    .filter((s) => (s.results?.length ?? 0) > 0);
}

export function sessionsJsonFingerprint(sessions) {
  try {
    return JSON.stringify(
      sessions.map((s) => ({
        id: s.id,
        createdAt: s.createdAt,
        results: (s.results || []).map((r) => r?.email?.id ?? null),
      }))
    );
  } catch {
    return '';
  }
}

/** Load sessions, rewrite storage once if duplicates were removed. */
export function loadSessionsNormalized() {
  const raw = loadSessions();
  const normalized = normalizeSessionsRemoveDuplicates(raw);
  if (sessionsJsonFingerprint(raw) !== sessionsJsonFingerprint(normalized)) {
    persistSessions(normalized);
  }
  return normalized;
}

export function collectAnalyzedEmailIds(sessions) {
  const ids = new Set();
  if (!Array.isArray(sessions)) return ids;
  for (const s of sessions) {
    for (const r of s.results || []) {
      const id = r?.email?.id;
      if (id) ids.add(id);
    }
  }
  return ids;
}
